import { MembershipType } from './account';
import type { BetslipBet, BetslipTicket } from './betslip';
import type { CreditBetSettings } from '@/api/auth';
import type { PlaceBetMessage, PlaceBetRequest } from './place-bet';
import type { TicketListItem } from './tickets';
import { TicketType } from './tickets';

/** Whether the logged-in player may use KreditBet (settings + membership). */
export function isKreditBetAllowed(
  creditBetSettings: CreditBetSettings | null | undefined,
  membership: number | null | undefined,
): boolean {
  if (!creditBetSettings?.AllowCreditBet) return false;
  if (
    membership === MembershipType.Anonymous ||
    membership === MembershipType.AnonymousReservation
  ) {
    return false;
  }
  return true;
}

/** Banner / Try it: allowed settings and a non-empty slip. */
export function canShowKreditBetBanner(
  ticket: BetslipTicket,
  creditBetSettings: CreditBetSettings | null | undefined,
  membership: number | null | undefined,
): boolean {
  if (!isKreditBetAllowed(creditBetSettings, membership)) return false;
  return (ticket.Items?.length ?? 0) > 0;
}

/** True when a creditor ticket is linked on the slip. */
export function hasKreditBetCreditor(ticket: BetslipTicket): boolean {
  return Boolean(ticket.CreditorNumber);
}

/** Block KreditBet when rewards are already on the slip (legacy mutual exclusion). */
export function kreditBetBlockedByRewards(ticket: BetslipTicket): PlaceBetMessage | null {
  if (Array.isArray(ticket.Rewards) && ticket.Rewards.length > 0) {
    return {
      key: 'CannotUsedCreditBetWithRewardBonus',
      defaultValue: 'Cannot use KreditBet with a reward bonus',
    };
  }
  return null;
}

/** Max stake while KreditBet is connected — creditor possible win. */
export function kreditBetMaxStake(ticket: BetslipTicket): number | null {
  if (!ticket.CreditorNumber) return null;
  const max = ticket.CreditorPossibleWin;
  if (typeof max !== 'number' || !Number.isFinite(max) || max <= 0) return null;
  return max;
}

/** Ticket type label for the selected creditor summary (Single / Combi / System). */
export function kreditBetCreditorTypeLabel(
  type: number | null | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  if (type === TicketType.Combi) {
    return t('Combi', { defaultValue: 'Combi' });
  }
  if (type === TicketType.System) {
    return t('System', { defaultValue: 'System' });
  }
  return t('Single', { defaultValue: 'Single' });
}

/** Main open ticket marked as creditor — cashout is blocked server-side. */
export function isKreditBetMainTicket(ticket: TicketListItem): boolean {
  return ticket.Creditor === true;
}

/** Credit-bet ticket linked to a main ticket number. */
export function isKreditBetChildTicket(ticket: TicketListItem): boolean {
  return Boolean(ticket.CreditorNumber) && ticket.Creditor !== true;
}

function stringifySpecifierDescription(bet: BetslipBet): BetslipBet {
  let next = bet;
  if (bet.SpecifierDescription != null && typeof bet.SpecifierDescription !== 'string') {
    next = {
      ...next,
      SpecifierDescription: JSON.stringify(bet.SpecifierDescription),
    };
  }
  if (next.Custom && next.Selections?.length) {
    next = {
      ...next,
      Selections: next.Selections.map((sel) => {
        if (sel.SpecifierDescription == null || typeof sel.SpecifierDescription === 'string') {
          return sel;
        }
        return {
          ...sel,
          SpecifierDescription: JSON.stringify(sel.SpecifierDescription),
        };
      }),
    };
  }
  return next;
}

/**
 * Body for `sb/api/tickets/creditorlist`.
 * UsersAPI resolves the player with `TicketRequest.User` (= machine GUID); without it
 * the endpoint returns an empty list.
 */
export function buildCreditorListRequest(
  ticket: BetslipTicket,
  userGuid: string,
): PlaceBetRequest {
  const cloned = structuredClone(ticket);
  cloned.Items = cloned.Items.map((item) => {
    const eventIdRaw = item.Event.EventId;
    const eventIdNum =
      typeof eventIdRaw === 'number'
        ? eventIdRaw
        : Number.parseInt(String(eventIdRaw), 10);
    return {
      ...item,
      Event: {
        ...item.Event,
        // ASP.NET binds EventId as int for overlap filtering.
        EventId: Number.isFinite(eventIdNum) ? eventIdNum : eventIdRaw,
      },
      Bets: item.Bets.map(stringifySpecifierDescription),
    };
  });

  return {
    ...cloned,
    AccountGuid: '',
    CurrencyCode: cloned.CurrencyCode ?? '',
    Language: cloned.Language ?? '',
    User: userGuid,
    IsBonus: false,
    StakeBonus: 0,
    StakeNormal: cloned.Stake,
    IsMobile: false,
  };
}
